// Pure model shared by the custom-field-aware lists (Opportunities, Projects): definitions as the picker
// and the columns read them, per-row values, what a cell shows and what it sorts on. No React and no
// design-system imports, so it is tested exhaustively without mounting - the same isolation
// opportunityTableRows.ts keeps.
import moment from 'moment';
import {dropdownOptionKey} from '../../util/customFieldValueValidation';

// Company.customFieldDefinitionsV2 (OPPORTUNITY_LIST) / projectListCustomFieldDefinitions as the page queries select them.
export interface CustomFieldDefinitionNode {
	id: string;
	key: string;
	displayName: string;
	type: string;
	readOnly?: boolean | null; // absent on DropdownCustomField, which cannot be read-only
	options?: ReadonlyArray<{id: string; displayName: string}> | null;
}
export interface CustomFieldDefinitionConnection {
	edges?: ReadonlyArray<{node?: CustomFieldDefinitionNode | null} | null> | null;
}

export type CustomFieldKind = 'TEXT' | 'DROPDOWN' | 'INTEGER' | 'DECIMAL' | 'DATE';

export interface CustomFieldColumnDef {
	// The definition's global id - the stable key saved choices use, so a rename changes nothing.
	id: string;
	// The key values are stored under; what the cell looks up and the editor writes.
	key: string;
	displayName: string;
	kind: CustomFieldKind;
	readOnly: boolean;
	// `key` is the decoded numeric id - the form a dropdown value is stored in.
	options: Array<{id: string; key: string; displayName: string}>;
}

export const CUSTOM_FIELD_COLUMN_PREFIX = 'customField_';
export const customFieldColumnId = (def: CustomFieldColumnDef): string => `${CUSTOM_FIELD_COLUMN_PREFIX}${def.id}`;
export const isCustomFieldColumnId = (id: string): boolean => id.startsWith(CUSTOM_FIELD_COLUMN_PREFIX);

// The service and graphql say TEXT; the web-front constants say STRING. Both mean a text field.
const toKind = (type: string): CustomFieldKind => {
	switch (type) {
		case 'DROPDOWN':
		case 'INTEGER':
		case 'DECIMAL':
		case 'DATE':
			return type;
		default:
			return 'TEXT';
	}
};

const byNameAZ = (a: CustomFieldColumnDef, b: CustomFieldColumnDef): number =>
	a.displayName.localeCompare(b.displayName, undefined, {sensitivity: 'base'});

/** Definitions in A-Z order. */
export function toCustomFieldColumnDefs(
	connection: CustomFieldDefinitionConnection | null | undefined
): CustomFieldColumnDef[] {
	return (connection?.edges ?? [])
		.map(edge => edge?.node)
		.filter((node): node is NonNullable<typeof node> => Boolean(node))
		.map(node => ({
			id: node.id,
			key: node.key,
			displayName: node.displayName,
			kind: toKind(node.type),
			readOnly: node.readOnly === true,
			options: (node.options ?? []).map(option => ({
				id: option.id,
				key: dropdownOptionKey(option.id),
				displayName: option.displayName,
			})),
		}))
		.sort(byNameAZ);
}

// The per-entity values connection a list query aliases (opportunityListCustomFields,
// projectListCustomFields, ...). Only key and value are read.
export interface CustomFieldValueConnection {
	edges?: ReadonlyArray<{node?: {key: string; value?: string | null} | null} | null> | null;
}

export function customFieldValuesFrom(
	connection: CustomFieldValueConnection | null | undefined
): Record<string, string | null> {
	const values: Record<string, string | null> = {};
	(connection?.edges ?? []).forEach(edge => {
		if (edge?.node) values[edge.node.key] = edge.node.value ?? null;
	});
	return values;
}

// Every custom field column is this wide, on every list.
export const CUSTOM_FIELD_COLUMN_WIDTH_PX = 160;

const isEmpty = (raw: string | null | undefined): raw is null | undefined | '' => raw == null || raw === '';
const optionName = (def: CustomFieldColumnDef, raw: string): string =>
	def.options.find(option => option.key === raw)?.displayName ?? '';
const decimalsOf = (raw: string): number => (raw.includes('.') ? raw.split('.')[1].length : 0);

// A stored number as exact parts, never through a float: Number() rounds past 2^53 and past about 17
// significant digits, so long values would display and sort rounded. Leading integer zeros and trailing
// fraction zeros are dropped, and zero is never negative, so equal values have equal parts.
interface NumberParts {
	negative: boolean;
	integer: string;
	fraction: string;
}
const NUMBER_PATTERN = /^([+-]?)(\d*)(?:\.(\d*))?$/;
const parseNumber = (raw: string): NumberParts | undefined => {
	const match = NUMBER_PATTERN.exec(raw.trim());
	if (!match || (match[2] === '' && !match[3])) return undefined;
	const integer = match[2].replace(/^0+/, '') || '0';
	const fraction = (match[3] ?? '').replace(/0+$/, '');
	const negative = match[1] === '-' && (integer !== '0' || fraction !== '');
	return {negative, integer, fraction};
};
const partsToString = ({negative, integer, fraction}: NumberParts): string =>
	`${negative ? '-' : ''}${integer}${fraction ? `.${fraction}` : ''}`;

// Compares two canonical number strings (partsToString output) exactly: sign, then integer length, then
// the integer digits, then the fraction digits padded to the same length.
const compareNumberStrings = (a: string, b: string): number => {
	const x = parseNumber(a) as NumberParts;
	const y = parseNumber(b) as NumberParts;
	if (x.negative !== y.negative) return x.negative ? -1 : 1;
	const sign = x.negative ? -1 : 1;
	if (x.integer.length !== y.integer.length) return x.integer.length < y.integer.length ? -sign : sign;
	if (x.integer !== y.integer) return x.integer < y.integer ? -sign : sign;
	const width = Math.max(x.fraction.length, y.fraction.length);
	const fx = x.fraction.padEnd(width, '0');
	const fy = y.fraction.padEnd(width, '0');
	return fx === fy ? 0 : fx < fy ? -sign : sign;
};

// Building an Intl.NumberFormat is far slower than using one, and every number cell asks for one on every
// render. Locale and decimals are few, so one formatter per pair is kept for the life of the page.
const numberFormats = new Map<string, Intl.NumberFormat>();
const numberFormatFor = (locale: string, decimals: number): Intl.NumberFormat => {
	const cacheKey = `${locale}|${decimals}`;
	let format = numberFormats.get(cacheKey);
	if (!format) {
		format = new Intl.NumberFormat(locale, {minimumFractionDigits: decimals, maximumFractionDigits: decimals});
		numberFormats.set(cacheKey, format);
	}
	return format;
};

// The decimal symbol a formatter writes, so it can be compared with the viewer's own separator. Kept per
// formatter, which is itself kept for the life of the page.
const decimalSymbols = new WeakMap<Intl.NumberFormat, string>();
const decimalSymbolOf = (format: Intl.NumberFormat): string => {
	let symbol = decimalSymbols.get(format);
	if (symbol === undefined) {
		// Probed on a plain formatter for the same locale: an integer formatter writes no decimal symbol.
		symbol =
			new Intl.NumberFormat(format.resolvedOptions().locale).formatToParts(1.5).find(part => part.type === 'decimal')
				?.value ?? '.';
		decimalSymbols.set(format, symbol);
	}
	return symbol;
};

// Writes a formatted number with the viewer's decimal separator, and the other symbol for thousands, when
// the formatter's locale uses a different one. The display locale and the profile language can disagree
// (an English-EU profile formats as en-GB but types decimals with a comma), and a value shown as "1,500"
// that the editor reads as 1.5 would be saved wrong when typed back.
const withDecimalSeparator = (format: Intl.NumberFormat, value: string, decimalSeparator?: ',' | '.'): string => {
	// The string itself is formatted, not Number(value), so long values keep every digit: format() takes a
	// decimal string exactly in current engines. TypeScript 4.6's lib only types number | bigint, hence the cast.
	const input = value as unknown as number;
	if (!decimalSeparator || decimalSymbolOf(format) === decimalSeparator) return format.format(input);
	const groupSeparator = decimalSeparator === ',' ? '.' : ',';
	return format
		.formatToParts(input)
		.map(part => (part.type === 'decimal' ? decimalSeparator : part.type === 'group' ? groupSeparator : part.value))
		.join('');
};

/** What the cell shows. Empty is blank - no dash - like the Client and Contact columns. Numbers use the
 * viewer's decimal separator when one is given - the one the inline editor reads - see withDecimalSeparator. */
export function formatCustomFieldValue(
	def: CustomFieldColumnDef,
	raw: string | null | undefined,
	locale: string,
	decimalSeparator?: ',' | '.'
): string {
	if (isEmpty(raw)) return '';
	switch (def.kind) {
		case 'DROPDOWN':
			return optionName(def, raw);
		case 'INTEGER':
		case 'DECIMAL': {
			if (Number.isNaN(Number(raw))) return raw;
			const decimals = def.kind === 'INTEGER' ? 0 : Math.min(decimalsOf(raw), 20);
			return withDecimalSeparator(numberFormatFor(locale, decimals), raw.trim(), decimalSeparator);
		}
		case 'DATE': {
			const date = moment(raw, 'YYYY-MM-DD', true);
			return date.isValid() ? date.format('ll') : raw;
		}
		default:
			return raw;
	}
}

/**
 * What the column sorts on - compare two of these with compareCustomFieldSortValues. Numbers are exact
 * canonical strings, dates timestamps, text and dropdowns lower-cased names. `undefined` for an empty value - the column sets sortUndefined: 'last',
 * which is the only way to keep blanks at the bottom in BOTH directions (the table flips a comparator's
 * sign for descending; `null` would not trip TanStack's undefined check).
 */
export function customFieldSortValue(def: CustomFieldColumnDef, raw: string | null | undefined): string | number | undefined {
	if (isEmpty(raw)) return undefined;
	switch (def.kind) {
		case 'INTEGER':
		case 'DECIMAL': {
			const parts = parseNumber(raw);
			return parts ? partsToString(parts) : undefined;
		}
		case 'DATE': {
			const date = moment(raw, 'YYYY-MM-DD', true);
			return date.isValid() ? date.valueOf() : undefined;
		}
		case 'DROPDOWN': {
			const name = optionName(def, raw);
			return name ? name.toLowerCase() : undefined;
		}
		default:
			return raw.toLowerCase();
	}
}

/** Three-way comparator for two (non-empty) customFieldSortValue results of one definition. */
export function compareCustomFieldSortValues(def: CustomFieldColumnDef): (x: string | number, y: string | number) => number {
	if (def.kind === 'INTEGER' || def.kind === 'DECIMAL') {
		return (x, y) => compareNumberStrings(String(x), String(y));
	}
	// Explicit three-way rather than `a - b`, for the same NaN reason as cmpNum.
	return (x, y) => (x < y ? -1 : x > y ? 1 : 0);
}
